import React, { useState } from 'react'
import { Platform } from 'react-native'

import AspectRatio from 'app/components/base/AspectRatio'
import Box from 'app/components/base/Box'
import Image from 'app/components/base/Image'
import Pressable from 'app/components/base/Pressable'
import Skeleton from 'app/components/base/Skeleton'
import SwiperItems from 'app/components/base/SwiperItems'
import LikeButton from 'app/components/buttons/LikeButton'
import useModal from 'app/hooks/useModal'
import useAppStore from 'app/store/app.store'

import { ImageGalleryDialog } from '../components/ImageGalleryDynamicComponents'

interface ImageSliderProps {
  images: Attachment[]
  isMobile?: boolean
  listingId: string
  isLiked?: boolean
  listingUserId?: string
  loading?: boolean
}

const ImageSlider: RFC<ImageSliderProps> = ({
  images,
  isMobile,
  listingId,
  isLiked,
  listingUserId,
  loading
}) => {
  const { open, showModal, hideModal } = useModal()
  const isTablet = useAppStore((state) => state.isTablet)

  const [activeIndex, setActiveIndex] = useState(0)
  const [currentLoadingIndex, setCurrentLoadingIndex] = useState(0)

  return (
    <Skeleton
      loading={loading}
      height={isTablet ? 500 : 375}
      width={isTablet ? 600 : 375}
      sx={{ ...Platform.select({ web: { transform: 'scale(1, 1)' } }) }}
    >
      <Box sx={{ position: 'relative', width: '100%' }}>
        <Box
          sx={{
            ...Platform.select({
              web: {
                maxHeight: { xs: '100%', md: 375 },
                maxWidth: { xs: '100%', md: 375 },
                width: '100vw',
                border: { xs: 'none', md: '1px solid' },
                borderColor: { xs: 'inherit', md: 'stroke.main' },
                borderRadius: { xs: 'initial', md: '8px' },
                overflow: { xs: 'auto', md: 'hidden' },
                '&:hover': { cursor: 'zoom-in' }
              },
              native: {
                width: '100%',
                ...(isTablet && { maxWidth: 600 })
              }
            })
          }}
        >
          <SwiperItems activeIndex={activeIndex} onChangeIndex={setActiveIndex}>
            {images?.map((item, index) => {
              const isFirstImage = index === 0

              return (
                <Box
                  key={item.id}
                  sx={{ width: '100%', height: '100%', position: 'relative' }}
                >
                  <Pressable onClick={showModal}>
                    <AspectRatio ratio={1 / 1}>
                      <Image
                        src={item.url}
                        alt={item.name}
                        fill
                        objectFit="contain"
                        priority={isFirstImage}
                        loading={
                          index <= currentLoadingIndex ? 'eager' : 'lazy'
                        }
                        onLoad={() => {
                          if (index === currentLoadingIndex) {
                            setCurrentLoadingIndex((prev) => prev + 1)
                          }
                        }}
                        {...Platform.select({
                          web: {
                            sizes:
                              '(max-width: 899px) 95vw, (max-width: 1280px) 30vw, 20vw'
                          },
                          native: { sizes: '640px' }
                        })}
                        placeholder="blur"
                      />
                    </AspectRatio>
                  </Pressable>
                </Box>
              )
            })}
          </SwiperItems>

          <LikeButton
            listingId={listingId}
            isLiked={isLiked}
            listingUserId={listingUserId}
            sx={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              zIndex: 100,
              backgroundColor: 'background.default'
            }}
          />
        </Box>

        {open && (
          <ImageGalleryDialog
            open={open}
            onClose={hideModal}
            items={images || []}
            showNav={!isMobile}
            index={activeIndex}
          />
        )}
      </Box>
    </Skeleton>
  )
}
export default ImageSlider
